/**
 * Export and Auto-Save the Conversation (feature guide)
 *
 * Screenshots were taken from the Sokuji v0.41.1 desktop app.
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const en: TutorialData = {
  "pageTitle": "Export and Auto-Save the Conversation",
  "backLink": "Back to Documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Overview",
    "content": "Sokuji can copy the conversation to the clipboard or save it as a text or JSON file, and can save it for you every time a session ends."
  },
  "steps": [
    {
      "title": "Open the export menu",
      "content": "Click the download button in the toolbar of the main window (in the desktop app, the subtitle window has one too).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Choose what to include",
      "content": "Under <strong>Include</strong>, choose whether to keep the original (<strong>Src</strong>) and the translation (<strong>Trans</strong>) for Me and for Other. The choices start from what the conversation currently shows."
    },
    {
      "title": "Copy or download",
      "content": "<strong>Copy to clipboard</strong>, <strong>Download as .txt</strong> or <strong>Download as .json</strong>. Files are named like <code>sokuji-conversation-20260928-152728.txt</code>. The text file starts with the time of export, the provider, the models and the languages, then one block per exchange: a line with the time and the speaker, the original on the next line, and the translation after an arrow: <br><code>[15:26:56] Me<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Other<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>When the other side has two or more speakers (Soniox and KizunaAI), their blocks are headed <strong>Speaker 1</strong>, <strong>Speaker 2</strong> and so on instead of Other."
    },
    {
      "title": "Save automatically",
      "content": "Switch on <strong>Auto-save when session ends</strong>. The desktop app then saves the whole conversation as a .txt file in your Downloads folder each time you stop, and shows a notice with <strong>Show in folder</strong>. The browser extension downloads the file instead. The file has the same layout and speaker names as <strong>Download as .txt</strong>."
    }
  ],
  "tips": {
    "title": "Tips",
    "items": [
      "Use the JSON file for other tools (format sokuji-conversation/2): one record per exchange, with its time, its side (you or the other side), the original and the translation, and a speaker number (person) when that side has two or more speakers.",
      "Export before you clear the conversation."
    ]
  },
  "faq": {
    "title": "Frequently Asked Questions",
    "items": [
      {
        "question": "Does auto-save run if I close the extension’s side panel?",
        "answer": "No. Stop the session first, then close the panel."
      },
      {
        "question": "Where does the desktop app save the file?",
        "answer": "In your Downloads folder. If a file with the same name exists, Sokuji adds a number to the new one."
      }
    ]
  },
  "troubleshooting": {
    "title": "Troubleshooting",
    "content": "Still stuck? Send us feedback from the account menu, or write to <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. You can also ask in <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const zh: TutorialData = {
  "pageTitle": "导出和自动保存对话",
  "backLink": "返回文档",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概述",
    "content": "Sokuji 可以把对话复制到剪贴板，或保存为文本或 JSON 文件，还可以在每次会话结束时自动为你保存。"
  },
  "steps": [
    {
      "title": "打开导出菜单",
      "content": "点击主窗口工具栏中的下载按钮（在桌面应用中，字幕窗口也有一个）。",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "选择要包含的内容",
      "content": "在<strong>包含</strong>下，分别为我和对方选择是否保留<strong>原文</strong>和<strong>译文</strong>。初始选项与对话当前显示的内容一致。"
    },
    {
      "title": "复制或下载",
      "content": "<strong>复制到剪贴板</strong>、<strong>下载为 .txt</strong> 或<strong>下载为 .json</strong>。文件名类似 <code>sokuji-conversation-20260928-152728.txt</code>。文本文件开头是生成时间、服务商、模型和语言，之后每轮发言一块：第一行是时间和说话方，下一行是原文，再下一行是以 → 开头的译文：<br><code>[15:26:56] 我<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] 对方<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>当对方有两个或更多说话人时（Soniox 和 KizunaAI），他们的块会标为<strong>说话人 1</strong>、<strong>说话人 2</strong>等，而不是“对方”。"
    },
    {
      "title": "自动保存",
      "content": "开启<strong>会话结束时自动保存</strong>。之后每次停止会话时，桌面应用都会把整段对话以 .txt 文件保存到你的“下载”文件夹，并显示带有<strong>在文件夹中显示</strong>的通知。浏览器扩展则会下载该文件。文件的格式和说话人名称与<strong>下载为 .txt</strong>相同。"
    }
  ],
  "tips": {
    "title": "提示",
    "items": [
      "需要在其他工具中使用时请选择 JSON 文件（格式 sokuji-conversation/2）：每轮发言一条记录，包含时间、说话方（你或对方）、原文和译文；当这一方有两个或更多说话人时，还包含说话人编号（person）。",
      "请在清除对话之前导出。"
    ]
  },
  "faq": {
    "title": "常见问题",
    "items": [
      {
        "question": "如果我关闭扩展的侧边栏，自动保存还会执行吗？",
        "answer": "不会。请先停止会话，再关闭侧边栏。"
      },
      {
        "question": "桌面应用把文件保存在哪里？",
        "answer": "保存在你的“下载”文件夹中。如果已存在同名文件，Sokuji 会在新文件名中加上编号。"
      }
    ]
  },
  "troubleshooting": {
    "title": "故障排除",
    "content": "仍然无法解决？可以从账号菜单向我们发送反馈，或发送邮件至 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>。你也可以在 <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> 中提问。"
  }
};

const ja: TutorialData = {
  "pageTitle": "会話のエクスポートと自動保存",
  "backLink": "ドキュメントに戻る",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "概要",
    "content": "Sokuji は会話をクリップボードにコピーしたり、テキストファイルや JSON ファイルとして保存したりできます。セッションが終わるたびに自動で保存することもできます。"
  },
  "steps": [
    {
      "title": "エクスポートメニューを開く",
      "content": "メイン画面のツールバーにあるダウンロードボタンをクリックします（デスクトップアプリでは字幕ウィンドウにもあります）。",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "含める内容を選ぶ",
      "content": "<strong>含める</strong>で、自分と相手それぞれについて<strong>原文</strong>と<strong>訳文</strong>を残すかどうかを選びます。最初は会話に現在表示されている内容が選ばれています。"
    },
    {
      "title": "コピーまたはダウンロードする",
      "content": "<strong>クリップボードにコピー</strong>、<strong>.txt でダウンロード</strong>、<strong>.json でダウンロード</strong>のいずれかを選びます。ファイル名は <code>sokuji-conversation-20260928-152728.txt</code> のようになります。テキストファイルはエクスポートした時刻、プロバイダー、モデル、言語から始まり、その後に発言ごとに 1 ブロックが続きます。時刻と話者の行、次の行に原文、矢印の後に翻訳です：<br><code>[15:26:56] 自分<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] 相手<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>相手側に 2 人以上の話者がいる場合（Soniox と KizunaAI）、そのブロックの見出しは「相手」ではなく<strong>話者 1</strong>、<strong>話者 2</strong>などになります。"
    },
    {
      "title": "自動で保存する",
      "content": "<strong>セッション終了時に自動保存</strong>をオンにします。すると、デスクトップアプリは停止するたびに会話全体を .txt ファイルとして「ダウンロード」フォルダに保存し、<strong>フォルダで表示</strong>付きの通知を表示します。ブラウザー拡張機能では代わりにファイルがダウンロードされます。ファイルのレイアウトと話者名は<strong>.txt でダウンロード</strong>と同じです。"
    }
  ],
  "tips": {
    "title": "ヒント",
    "items": [
      "ほかのツールで使うなら JSON ファイルが便利です（形式は sokuji-conversation/2）。発言ごとに 1 レコードで、時刻、どちら側の発言か（あなたか相手か）、原文、翻訳が含まれ、その側に 2 人以上の話者がいる場合は話者番号（person）も含まれます。",
      "会話をクリアする前にエクスポートしてください。"
    ]
  },
  "faq": {
    "title": "よくある質問",
    "items": [
      {
        "question": "拡張機能のサイドパネルを閉じても自動保存されますか？",
        "answer": "いいえ。先にセッションを停止してから、パネルを閉じてください。"
      },
      {
        "question": "デスクトップアプリはどこにファイルを保存しますか？",
        "answer": "「ダウンロード」フォルダです。同じ名前のファイルがすでにある場合、Sokuji は新しいファイルに番号を付けます。"
      }
    ]
  },
  "troubleshooting": {
    "title": "トラブルシューティング",
    "content": "それでも解決しない場合は、アカウントメニューからフィードバックを送るか、<a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a> までご連絡ください。<a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> で質問することもできます。"
  }
};

const ko: TutorialData = {
  "pageTitle": "대화 내보내기 및 자동 저장",
  "backLink": "문서로 돌아가기",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "개요",
    "content": "Sokuji는 대화를 클립보드에 복사하거나 텍스트 또는 JSON 파일로 저장할 수 있으며, 세션이 끝날 때마다 자동으로 저장할 수도 있습니다."
  },
  "steps": [
    {
      "title": "내보내기 메뉴 열기",
      "content": "메인 창 도구 모음의 다운로드 버튼을 클릭하세요(데스크톱 앱에서는 자막 창에도 있습니다).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "포함할 내용 선택",
      "content": "<strong>포함</strong>에서 나와 상대방 각각에 대해 <strong>원문</strong>과 <strong>번역</strong>을 남길지 선택하세요. 처음에는 대화에 현재 표시된 내용대로 선택되어 있습니다."
    },
    {
      "title": "복사 또는 다운로드",
      "content": "<strong>클립보드에 복사</strong>, <strong>.txt로 다운로드</strong> 또는 <strong>.json으로 다운로드</strong>를 선택하세요. 파일 이름은 <code>sokuji-conversation-20260928-152728.txt</code>와 같은 형식입니다. 텍스트 파일은 내보낸 시각, 제공업체, 모델, 언어로 시작하고, 그 뒤에 발화마다 한 블록씩 이어집니다. 첫 줄은 시각과 화자, 다음 줄은 원문, 그다음 줄은 화살표 뒤의 번역입니다. <br><code>[15:26:56] 나<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] 상대방<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>상대방 쪽에 화자가 두 명 이상이면(Soniox와 KizunaAI) 그 블록의 제목은 상대방 대신 <strong>화자 1</strong>, <strong>화자 2</strong> 등이 됩니다."
    },
    {
      "title": "자동 저장",
      "content": "<strong>세션 종료 시 자동 저장</strong>을 켜세요. 그러면 데스크톱 앱은 세션을 중지할 때마다 전체 대화를 .txt 파일로 다운로드 폴더에 저장하고, <strong>폴더에서 보기</strong>가 있는 알림을 표시합니다. 브라우저 확장 프로그램은 대신 파일을 다운로드합니다. 파일의 형식과 화자 이름은 <strong>.txt로 다운로드</strong>와 같습니다."
    }
  ],
  "tips": {
    "title": "팁",
    "items": [
      "다른 도구에서 쓰려면 JSON 파일(형식 sokuji-conversation/2)을 사용하세요. 발화마다 한 레코드씩, 시간, 어느 쪽의 발화인지(나 또는 상대방), 원문과 번역이 담겨 있으며, 그쪽에 화자가 두 명 이상이면 화자 번호(person)도 담겨 있습니다.",
      "대화를 지우기 전에 내보내세요."
    ]
  },
  "faq": {
    "title": "자주 묻는 질문",
    "items": [
      {
        "question": "확장 프로그램의 사이드 패널을 닫아도 자동 저장되나요?",
        "answer": "아니요. 먼저 세션을 중지한 다음 패널을 닫으세요."
      },
      {
        "question": "데스크톱 앱은 파일을 어디에 저장하나요?",
        "answer": "다운로드 폴더에 저장합니다. 같은 이름의 파일이 이미 있으면 Sokuji가 새 파일 이름에 번호를 붙입니다."
      }
    ]
  },
  "troubleshooting": {
    "title": "문제 해결",
    "content": "그래도 해결되지 않나요? 계정 메뉴에서 피드백을 보내거나 <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>로 이메일을 보내 주세요. <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>에서 질문할 수도 있습니다."
  }
};

const es: TutorialData = {
  "pageTitle": "Exportar y guardar automáticamente la conversación",
  "backLink": "Volver a la documentación",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Descripción general",
    "content": "Sokuji puede copiar la conversación al portapapeles o guardarla como archivo de texto o JSON, y puede guardarla por ti cada vez que termina una sesión."
  },
  "steps": [
    {
      "title": "Abre el menú de exportación",
      "content": "Haz clic en el botón de descarga de la barra de herramientas de la ventana principal (en la aplicación de escritorio, la ventana de subtítulos también tiene uno).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Elige qué incluir",
      "content": "En <strong>Incluir</strong>, elige si conservar el original (<strong>Origen</strong>) y la traducción (<strong>Traducción</strong>) de Yo y de Otro. Las opciones parten de lo que muestra la conversación en ese momento."
    },
    {
      "title": "Copia o descarga",
      "content": "<strong>Copiar al portapapeles</strong>, <strong>Descargar como .txt</strong> o <strong>Descargar como .json</strong>. Los archivos se llaman, por ejemplo, <code>sokuji-conversation-20260928-152728.txt</code>. El archivo de texto empieza con la hora de exportación, el proveedor, los modelos y los idiomas, y después un bloque por intervención: una línea con la hora y el hablante, el original en la línea siguiente y la traducción tras una flecha: <br><code>[15:26:56] Yo<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Otro<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Cuando la otra parte tiene dos o más hablantes (Soniox y KizunaAI), sus bloques llevan como encabezado <strong>Hablante 1</strong>, <strong>Hablante 2</strong>, etc., en lugar de Otro."
    },
    {
      "title": "Guarda automáticamente",
      "content": "Activa <strong>Guardar automáticamente al terminar la sesión</strong>. La aplicación de escritorio guarda entonces toda la conversación como archivo .txt en tu carpeta de Descargas cada vez que detienes la sesión, y muestra un aviso con <strong>Mostrar en la carpeta</strong>. La extensión de navegador, en cambio, descarga el archivo. El archivo tiene la misma estructura y los mismos nombres de hablante que <strong>Descargar como .txt</strong>."
    }
  ],
  "tips": {
    "title": "Consejos",
    "items": [
      "Usa el archivo JSON para otras herramientas (formato sokuji-conversation/2): un registro por intervención, con su hora, su lado (tú o la otra parte), el original y la traducción, y un número de hablante (person) cuando ese lado tiene dos o más hablantes.",
      "Exporta antes de borrar la conversación."
    ]
  },
  "faq": {
    "title": "Preguntas frecuentes",
    "items": [
      {
        "question": "¿Se guarda automáticamente si cierro el panel lateral de la extensión?",
        "answer": "No. Detén primero la sesión y luego cierra el panel."
      },
      {
        "question": "¿Dónde guarda el archivo la aplicación de escritorio?",
        "answer": "En tu carpeta de Descargas. Si ya existe un archivo con el mismo nombre, Sokuji añade un número al nuevo."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solución de problemas",
    "content": "¿Sigues atascado? Envíanos tus comentarios desde el menú de la cuenta o escribe a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. También puedes preguntar en <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const fr: TutorialData = {
  "pageTitle": "Exporter et enregistrer automatiquement la conversation",
  "backLink": "Retour à la documentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Aperçu",
    "content": "Sokuji peut copier la conversation dans le presse-papiers ou l’enregistrer dans un fichier texte ou JSON, et peut l’enregistrer pour vous à la fin de chaque session."
  },
  "steps": [
    {
      "title": "Ouvrez le menu d’export",
      "content": "Cliquez sur le bouton de téléchargement dans la barre d’outils de la fenêtre principale (dans l’application de bureau, la fenêtre de sous-titres en a un aussi).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Choisissez le contenu",
      "content": "Sous <strong>Inclure</strong>, choisissez de garder ou non l’original (<strong>Source</strong>) et la traduction (<strong>Traduction</strong>) pour Moi et pour Autre. Les choix partent de ce que la conversation affiche actuellement."
    },
    {
      "title": "Copiez ou téléchargez",
      "content": "<strong>Copier dans le presse-papiers</strong>, <strong>Télécharger en .txt</strong> ou <strong>Télécharger en .json</strong>. Les fichiers portent un nom comme <code>sokuji-conversation-20260928-152728.txt</code>. Le fichier texte commence par l’heure de l’export, le fournisseur, les modèles et les langues, puis un bloc par échange : une ligne avec l’heure et le locuteur, l’original sur la ligne suivante, et la traduction après une flèche : <br><code>[15:26:56] Moi<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Autre<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Quand il y a deux intervenants ou plus de l’autre côté (Soniox et KizunaAI), leurs blocs sont intitulés <strong>Intervenant 1</strong>, <strong>Intervenant 2</strong>, etc., au lieu d’Autre."
    },
    {
      "title": "Enregistrez automatiquement",
      "content": "Activez <strong>Enregistrer automatiquement en fin de session</strong>. L’application de bureau enregistre alors toute la conversation dans un fichier .txt de votre dossier Téléchargements chaque fois que vous arrêtez la session, et affiche un avis avec <strong>Afficher dans le dossier</strong>. L’extension de navigateur, elle, télécharge le fichier. Le fichier a la même présentation et les mêmes noms d’intervenants que <strong>Télécharger en .txt</strong>."
    }
  ],
  "tips": {
    "title": "Conseils",
    "items": [
      "Utilisez le fichier JSON pour d’autres outils (format sokuji-conversation/2) : un enregistrement par échange, avec son heure, son côté (vous ou votre interlocuteur), l’original et la traduction, et un numéro d’intervenant (person) quand ce côté compte deux intervenants ou plus.",
      "Exportez avant d’effacer la conversation."
    ]
  },
  "faq": {
    "title": "Questions fréquentes",
    "items": [
      {
        "question": "L’enregistrement automatique a-t-il lieu si je ferme le panneau latéral de l’extension ?",
        "answer": "Non. Arrêtez d’abord la session, puis fermez le panneau."
      },
      {
        "question": "Où l’application de bureau enregistre-t-elle le fichier ?",
        "answer": "Dans votre dossier Téléchargements. Si un fichier du même nom existe déjà, Sokuji ajoute un numéro au nouveau."
      }
    ]
  },
  "troubleshooting": {
    "title": "Dépannage",
    "content": "Toujours bloqué ? Envoyez-nous un retour depuis le menu du compte, ou écrivez à <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Vous pouvez aussi poser votre question sur <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ar: TutorialData = {
  "pageTitle": "تصدير المحادثة وحفظها تلقائيًا",
  "backLink": "العودة إلى التوثيق",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "نظرة عامة",
    "content": "يستطيع Sokuji نسخ المحادثة إلى الحافظة أو حفظها كملف نصي أو ملف JSON، ويمكنه حفظها لك في كل مرة تنتهي فيها جلسة."
  },
  "steps": [
    {
      "title": "افتح قائمة التصدير",
      "content": "انقر على زر التنزيل في شريط أدوات النافذة الرئيسية (في تطبيق سطح المكتب، توجد واحدة في نافذة الترجمة النصية أيضًا).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "اختر ما تريد تضمينه",
      "content": "تحت <strong>تضمين</strong>، اختر ما إذا كنت تريد الاحتفاظ بـ<strong>الأصل</strong> و<strong>الترجمة</strong> لـ«أنا» ولـ«الآخر». تبدأ الخيارات بما تعرضه المحادثة حاليًا."
    },
    {
      "title": "انسخ أو نزّل",
      "content": "<strong>نسخ إلى الحافظة</strong> أو <strong>تنزيل بصيغة .txt</strong> أو <strong>تنزيل بصيغة .json</strong>. تُسمّى الملفات بأسماء مثل <code dir=\"ltr\">sokuji-conversation-20260928-152728.txt</code>. يبدأ الملف النصي بوقت التصدير ومزوّد الخدمة والنماذج واللغات، ثم كتلة لكل تبادل: سطر فيه الوقت والمتحدث، ثم النص الأصلي في السطر التالي، ثم الترجمة بعد سهم: <br><code dir=\"ltr\">[15:26:56] أنا<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] الآخر<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>عندما يكون في الطرف الآخر متحدثان أو أكثر (Soniox و KizunaAI)، تحمل كتلهم العناوين <strong>المتحدث 1</strong> و<strong>المتحدث 2</strong> وهكذا بدلًا من الآخر."
    },
    {
      "title": "احفظ تلقائيًا",
      "content": "فعّل <strong>حفظ تلقائي عند انتهاء الجلسة</strong>. بعد ذلك يحفظ تطبيق سطح المكتب المحادثة كاملة كملف .txt في مجلد التنزيلات في كل مرة تتوقف فيها، ويعرض إشعارًا فيه <strong>إظهار في المجلد</strong>. أما امتداد المتصفح فيُنزّل الملف بدلًا من ذلك. ويكون للملف التخطيط نفسه وأسماء المتحدثين نفسها كما في <strong>تنزيل بصيغة .txt</strong>."
    }
  ],
  "tips": {
    "title": "نصائح",
    "items": [
      "استخدم ملف JSON مع الأدوات الأخرى (الصيغة ⁦sokuji-conversation/2⁩): سجل واحد لكل تبادل، فيه وقته، وجانبه (أنت أو الطرف الآخر)، والنص الأصلي والترجمة، ورقم المتحدث (⁦person⁩) عندما يكون في ذلك الجانب متحدثان أو أكثر.",
      "صدّر المحادثة قبل أن تمسحها."
    ]
  },
  "faq": {
    "title": "الأسئلة الشائعة",
    "items": [
      {
        "question": "هل يعمل الحفظ التلقائي إذا أغلقت اللوحة الجانبية للامتداد؟",
        "answer": "لا. أوقف الجلسة أولًا، ثم أغلق اللوحة."
      },
      {
        "question": "أين يحفظ تطبيق سطح المكتب الملف؟",
        "answer": "في مجلد التنزيلات. إذا كان هناك ملف بالاسم نفسه، يضيف Sokuji رقمًا إلى الملف الجديد."
      }
    ]
  },
  "troubleshooting": {
    "title": "استكشاف الأخطاء وإصلاحها",
    "content": "هل ما زلت تواجه مشكلة؟ أرسل إلينا ملاحظاتك من قائمة الحساب، أو راسلنا على <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. يمكنك أيضًا طرح سؤالك في <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const pt: TutorialData = {
  "pageTitle": "Exportar e salvar a conversa automaticamente",
  "backLink": "Voltar à documentação",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Visão geral",
    "content": "O Sokuji pode copiar a conversa para a área de transferência ou salvá-la como arquivo de texto ou JSON, e pode salvá-la para você sempre que uma sessão termina."
  },
  "steps": [
    {
      "title": "Abra o menu de exportação",
      "content": "Clique no botão de download na barra de ferramentas da janela principal (no aplicativo de desktop, a janela de legendas também tem um).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Escolha o que incluir",
      "content": "Em <strong>Incluir</strong>, escolha se quer manter o original (<strong>Original</strong>) e a tradução (<strong>Tradução</strong>) de Eu e de Outro. As opções partem do que a conversa está mostrando no momento."
    },
    {
      "title": "Copie ou baixe",
      "content": "<strong>Copiar para área de transferência</strong>, <strong>Baixar como .txt</strong> ou <strong>Baixar como .json</strong>. Os arquivos recebem nomes como <code>sokuji-conversation-20260928-152728.txt</code>. O arquivo de texto começa com a hora da exportação, o provedor, os modelos e os idiomas e depois traz um bloco por fala: uma linha com a hora e quem falou, o original na linha seguinte e a tradução depois de uma seta: <br><code>[15:26:56] Eu<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Outro<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Quando o outro lado tem dois ou mais falantes (Soniox e KizunaAI), os blocos deles recebem o título <strong>Falante 1</strong>, <strong>Falante 2</strong> e assim por diante, em vez de Outro."
    },
    {
      "title": "Salve automaticamente",
      "content": "Ative <strong>Salvar automaticamente ao encerrar a sessão</strong>. O aplicativo de desktop passa a salvar a conversa inteira como arquivo .txt na sua pasta Downloads sempre que você encerra a sessão, e mostra um aviso com <strong>Mostrar na pasta</strong>. A extensão de navegador, em vez disso, baixa o arquivo. O arquivo tem o mesmo formato e os mesmos nomes de falantes que <strong>Baixar como .txt</strong>."
    }
  ],
  "tips": {
    "title": "Dicas",
    "items": [
      "Use o arquivo JSON em outras ferramentas (formato sokuji-conversation/2): um registro por fala, com o horário, o lado (você ou a outra pessoa), o original e a tradução, e um número de falante (person) quando esse lado tem dois ou mais falantes.",
      "Exporte antes de limpar a conversa."
    ]
  },
  "faq": {
    "title": "Perguntas frequentes",
    "items": [
      {
        "question": "O salvamento automático acontece se eu fechar o painel lateral da extensão?",
        "answer": "Não. Encerre a sessão primeiro e depois feche o painel."
      },
      {
        "question": "Onde o aplicativo de desktop salva o arquivo?",
        "answer": "Na sua pasta Downloads. Se já existir um arquivo com o mesmo nome, o Sokuji acrescenta um número ao novo."
      }
    ]
  },
  "troubleshooting": {
    "title": "Solução de problemas",
    "content": "Ainda com dificuldades? Envie seu feedback pelo menu da conta ou escreva para <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Você também pode perguntar no <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const ru: TutorialData = {
  "pageTitle": "Экспорт и автосохранение разговора",
  "backLink": "Назад к документации",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Обзор",
    "content": "Sokuji может скопировать разговор в буфер обмена или сохранить его в текстовый или JSON-файл, а также сохранять его за вас по окончании каждого сеанса."
  },
  "steps": [
    {
      "title": "Откройте меню экспорта",
      "content": "Нажмите кнопку загрузки на панели инструментов главного окна (в настольном приложении такая есть и в окне субтитров).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Выберите, что включить",
      "content": "В разделе <strong>Включить</strong> выберите, сохранять ли оригинал (<strong>Оригинал</strong>) и перевод (<strong>Перевод</strong>) для «Я» и для «Собеседник». Исходно выбрано то, что сейчас показывает разговор."
    },
    {
      "title": "Скопируйте или скачайте",
      "content": "<strong>Копировать в буфер обмена</strong>, <strong>Скачать как .txt</strong> или <strong>Скачать как .json</strong>. Файлы называются, например, <code>sokuji-conversation-20260928-152728.txt</code>. Текстовый файл начинается со времени экспорта, провайдера, моделей и языков, затем идёт по одному блоку на реплику: строка со временем и говорящим, на следующей строке оригинал, а после стрелки — перевод: <br><code>[15:26:56] Я<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Собеседник<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Когда на стороне собеседника двое или более говорящих (Soniox и KizunaAI), их блоки озаглавлены <strong>Говорящий 1</strong>, <strong>Говорящий 2</strong> и так далее вместо «Собеседник»."
    },
    {
      "title": "Сохраняйте автоматически",
      "content": "Включите <strong>Автосохранение по окончании сессии</strong>. Тогда настольное приложение при каждой остановке сохраняет весь разговор в .txt-файл в папке «Загрузки» и показывает уведомление с кнопкой <strong>Показать в папке</strong>. Расширение для браузера вместо этого скачивает файл. У файла та же структура и те же имена говорящих, что и у <strong>Скачать как .txt</strong>."
    }
  ],
  "tips": {
    "title": "Советы",
    "items": [
      "Используйте JSON-файл для других инструментов (формат sokuji-conversation/2): одна запись на реплику, с её временем, стороной (вы или собеседник), оригиналом и переводом, а также номером говорящего (person), когда на этой стороне двое или более говорящих.",
      "Экспортируйте, прежде чем очищать разговор."
    ]
  },
  "faq": {
    "title": "Часто задаваемые вопросы",
    "items": [
      {
        "question": "Срабатывает ли автосохранение, если закрыть боковую панель расширения?",
        "answer": "Нет. Сначала остановите сеанс, затем закройте панель."
      },
      {
        "question": "Куда настольное приложение сохраняет файл?",
        "answer": "В папку «Загрузки». Если файл с таким именем уже есть, Sokuji добавляет к новому номер."
      }
    ]
  },
  "troubleshooting": {
    "title": "Устранение неполадок",
    "content": "Всё ещё не получается? Отправьте нам отзыв через меню учётной записи или напишите на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Также можно задать вопрос в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const de: TutorialData = {
  "pageTitle": "Die Unterhaltung exportieren und automatisch speichern",
  "backLink": "Zurück zur Dokumentation",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Übersicht",
    "content": "Sokuji kann die Unterhaltung in die Zwischenablage kopieren oder als Text- oder JSON-Datei speichern und sie bei jedem Sitzungsende automatisch für Sie speichern."
  },
  "steps": [
    {
      "title": "Das Exportmenü öffnen",
      "content": "Klicken Sie auf die Download-Schaltfläche in der Symbolleiste des Hauptfensters (in der Desktop-App hat auch das Untertitelfenster eine).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Auswählen, was enthalten sein soll",
      "content": "Wählen Sie unter <strong>Einschließen</strong>, ob das Original (<strong>Original</strong>) und die Übersetzung (<strong>Übersetzung</strong>) für „Ich“ und für „Gegenüber“ enthalten sein sollen. Die Auswahl beginnt mit dem, was die Unterhaltung gerade anzeigt."
    },
    {
      "title": "Kopieren oder herunterladen",
      "content": "<strong>In Zwischenablage kopieren</strong>, <strong>Als .txt herunterladen</strong> oder <strong>Als .json herunterladen</strong>. Dateien heißen zum Beispiel <code>sokuji-conversation-20260928-152728.txt</code>. Die Textdatei beginnt mit dem Zeitpunkt des Exports, dem Anbieter, den Modellen und den Sprachen, dann folgt ein Block pro Gesprächsbeitrag: eine Zeile mit Zeit und Sprecher, in der nächsten Zeile das Original und nach einem Pfeil die Übersetzung: <br><code>[15:26:56] Ich<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Gegenüber<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Wenn auf der anderen Seite zwei oder mehr Personen sprechen (Soniox und KizunaAI), tragen deren Blöcke statt „Gegenüber“ die Überschrift <strong>Sprecher 1</strong>, <strong>Sprecher 2</strong> und so weiter."
    },
    {
      "title": "Automatisch speichern",
      "content": "Schalten Sie <strong>Bei Sitzungsende automatisch speichern</strong> ein. Die Desktop-App speichert dann bei jedem Beenden die gesamte Unterhaltung als .txt-Datei in Ihrem Downloads-Ordner und zeigt einen Hinweis mit <strong>Im Ordner anzeigen</strong>. Die Browsererweiterung lädt die Datei stattdessen herunter. Die Datei hat denselben Aufbau und dieselben Sprechernamen wie bei <strong>Als .txt herunterladen</strong>."
    }
  ],
  "tips": {
    "title": "Tipps",
    "items": [
      "Nutzen Sie die JSON-Datei für andere Werkzeuge (Format sokuji-conversation/2): ein Datensatz pro Gesprächsbeitrag, mit Zeit, Seite (Sie oder Ihr Gegenüber), Original und Übersetzung sowie einer Sprechernummer (person), wenn auf dieser Seite zwei oder mehr Personen sprechen.",
      "Exportieren Sie, bevor Sie die Unterhaltung löschen."
    ]
  },
  "faq": {
    "title": "Häufig gestellte Fragen",
    "items": [
      {
        "question": "Wird automatisch gespeichert, wenn ich die Seitenleiste der Erweiterung schließe?",
        "answer": "Nein. Beenden Sie zuerst die Sitzung und schließen Sie dann die Seitenleiste."
      },
      {
        "question": "Wo speichert die Desktop-App die Datei?",
        "answer": "In Ihrem Downloads-Ordner. Gibt es schon eine Datei mit demselben Namen, hängt Sokuji an die neue eine Nummer an."
      }
    ]
  },
  "troubleshooting": {
    "title": "Fehlerbehebung",
    "content": "Kommen Sie nicht weiter? Senden Sie uns Feedback über das Kontomenü oder schreiben Sie an <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Sie können auch in den <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a> fragen."
  }
};

const uk: TutorialData = {
  "pageTitle": "Експорт і автозбереження розмови",
  "backLink": "Назад до документації",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Огляд",
    "content": "Sokuji може скопіювати розмову в буфер обміну або зберегти її в текстовий чи JSON-файл, а також зберігати її за вас після завершення кожного сеансу."
  },
  "steps": [
    {
      "title": "Відкрийте меню експорту",
      "content": "Натисніть кнопку завантаження на панелі інструментів головного вікна (у настільному застосунку така є й у вікні субтитрів).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Виберіть, що включити",
      "content": "У розділі <strong>Включити</strong> виберіть, чи зберігати оригінал (<strong>Оригінал</strong>) і переклад (<strong>Переклад</strong>) для «Я» та для «Співрозмовник». Спочатку вибрано те, що зараз показує розмова."
    },
    {
      "title": "Скопіюйте або завантажте",
      "content": "<strong>Копіювати в буфер обміну</strong>, <strong>Завантажити як .txt</strong> або <strong>Завантажити як .json</strong>. Файли називаються, наприклад, <code>sokuji-conversation-20260928-152728.txt</code>. Текстовий файл починається з часу експорту, провайдера, моделей і мов, далі йде по одному блоку на репліку: рядок із часом і мовцем, оригінал у наступному рядку й переклад після стрілки: <br><code>[15:26:56] Я<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Співрозмовник<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Коли на боці співрозмовника двоє або більше мовців (Soniox і KizunaAI), їхні блоки мають заголовки <strong>Мовець 1</strong>, <strong>Мовець 2</strong> і так далі замість «Співрозмовник»."
    },
    {
      "title": "Зберігайте автоматично",
      "content": "Увімкніть <strong>Автозбереження після завершення сесії</strong>. Тоді настільний застосунок після кожної зупинки зберігає всю розмову в .txt-файл у теці «Завантаження» і показує сповіщення з кнопкою <strong>Показати в теці</strong>. Розширення для браузера натомість завантажує файл. Файл має ту саму структуру й ті самі імена мовців, що й <strong>Завантажити як .txt</strong>."
    }
  ],
  "tips": {
    "title": "Поради",
    "items": [
      "Використовуйте JSON-файл для інших інструментів (формат sokuji-conversation/2): по одному запису на репліку, з часом, стороною (ви чи співрозмовник), оригіналом і перекладом, а також номером мовця (person), коли на цьому боці двоє або більше мовців.",
      "Експортуйте, перш ніж очищати розмову."
    ]
  },
  "faq": {
    "title": "Часті запитання",
    "items": [
      {
        "question": "Чи спрацьовує автозбереження, якщо закрити бічну панель розширення?",
        "answer": "Ні. Спочатку зупиніть сеанс, потім закрийте панель."
      },
      {
        "question": "Куди настільний застосунок зберігає файл?",
        "answer": "У теку «Завантаження». Якщо файл із такою назвою вже існує, Sokuji додає до нового номер."
      }
    ]
  },
  "troubleshooting": {
    "title": "Усунення несправностей",
    "content": "Досі не виходить? Надішліть нам відгук через меню облікового запису або напишіть на <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Також можна запитати в <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const it: TutorialData = {
  "pageTitle": "Esportare e salvare automaticamente la conversazione",
  "backLink": "Torna alla documentazione",
  "backLinkUrl": "/docs",
  "overview": {
    "title": "Panoramica",
    "content": "Sokuji può copiare la conversazione negli appunti o salvarla come file di testo o JSON, e può salvarla per te ogni volta che una sessione termina."
  },
  "steps": [
    {
      "title": "Apri il menu di esportazione",
      "content": "Fai clic sul pulsante di download nella barra degli strumenti della finestra principale (nell’app desktop ce n’è uno anche nella finestra dei sottotitoli).",
      "screenshot": "01-export-menu.png"
    },
    {
      "title": "Scegli cosa includere",
      "content": "Sotto <strong>Includi</strong>, scegli se tenere l’originale (<strong>Originale</strong>) e la traduzione (<strong>Traduzione</strong>) per Io e per Altro. Le scelte partono da ciò che la conversazione mostra in quel momento."
    },
    {
      "title": "Copia o scarica",
      "content": "<strong>Copia negli appunti</strong>, <strong>Scarica come .txt</strong> o <strong>Scarica come .json</strong>. I file hanno nomi come <code>sokuji-conversation-20260928-152728.txt</code>. Il file di testo inizia con l’ora dell’esportazione, il fornitore, i modelli e le lingue, poi un blocco per ogni scambio: una riga con l’ora e chi parla, l’originale nella riga successiva e la traduzione dopo una freccia: <br><code>[15:26:56] Io<br>&nbsp;&nbsp;Hi everyone, today let’s go over the Q4 launch plan.<br>&nbsp;&nbsp;→ 皆さん、こんにちは。今日は第4四半期のリリース計画を確認しましょう。<br><br>[15:26:57] Altro<br>&nbsp;&nbsp;承知しました。リリース日は今も11月18日ですか？<br>&nbsp;&nbsp;→ Sounds good. Is the launch date still November 18?</code><br>Quando dall’altra parte ci sono due o più interlocutori (Soniox e KizunaAI), i loro blocchi sono intestati <strong>Interlocutore 1</strong>, <strong>Interlocutore 2</strong> e così via, al posto di Altro."
    },
    {
      "title": "Salva automaticamente",
      "content": "Attiva <strong>Salva automaticamente a fine sessione</strong>. L’app desktop salva così l’intera conversazione come file .txt nella cartella Download ogni volta che interrompi la sessione, e mostra un avviso con <strong>Mostra nella cartella</strong>. L’estensione per browser, invece, scarica il file. Il file ha la stessa struttura e gli stessi nomi di chi parla di <strong>Scarica come .txt</strong>."
    }
  ],
  "tips": {
    "title": "Consigli",
    "items": [
      "Usa il file JSON con altri strumenti (formato sokuji-conversation/2): un record per ogni scambio, con l’ora, la parte (tu o l’altra parte), l’originale e la traduzione, e un numero di interlocutore (person) quando quella parte ha due o più interlocutori.",
      "Esporta prima di cancellare la conversazione."
    ]
  },
  "faq": {
    "title": "Domande frequenti",
    "items": [
      {
        "question": "Il salvataggio automatico avviene se chiudo il pannello laterale dell’estensione?",
        "answer": "No. Interrompi prima la sessione, poi chiudi il pannello."
      },
      {
        "question": "Dove salva il file l’app desktop?",
        "answer": "Nella cartella Download. Se esiste già un file con lo stesso nome, Sokuji aggiunge un numero a quello nuovo."
      }
    ]
  },
  "troubleshooting": {
    "title": "Risoluzione dei problemi",
    "content": "Ancora bloccato? Inviaci un feedback dal menu dell’account, oppure scrivi a <a href=\"mailto:support@kizuna.ai\">support@kizuna.ai</a>. Puoi anche chiedere su <a href=\"https://github.com/kizuna-ai-lab/sokuji/discussions\">GitHub Discussions</a>."
  }
};

const translations: Record<Locale, TutorialData> = { en, zh, ja, ko, es, fr, ar, pt, ru, de, uk, it };

export function FeatureExport() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/features/export" />;
}
